import React from "react";
import { Button, Dialog, DialogContent, TextField } from "@mui/material";
import { ArrowBack, Close, ContentCopy } from "@mui/icons-material";
import { MuiOtpInput } from "mui-one-time-password-input";
import { ReactComponent as TwoFASuccessIcon } from "../../../assets/icons/TwoFASuccessIcon.svg";

const isSixDigitCode = (value) => /^\d{6}$/.test(String(value || ""));

const MfaSetupModal = ({
  open,
  onClose,
  mfaSetupStep,
  setMfaSetupStep,
  mfaSetup,
  isSettingUpMfa,
  isVerifyingMfa,
  mfaCode,
  mfaVerificationError,
  loading,
  fieldSx,
  handleCopySecret,
  handleContinueMfaSetup,
  handleMfaCodeChange,
  handleVerifyMfaCode,
}) => {
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "520px",
          borderRadius: "16px",
          margin: "24px",
        },
      }}
    >
      <DialogContent sx={{ p: 3.5 }}>
        {mfaSetupStep === "scan" ? (
          <div>

            <div className="flex justify-between items-center flex-row-reverse mb-2">
            <button
              type="button"
              onClick={onClose}
              className="text-primary border-2 border-primary/35 rounded-full hover:bg-primary hover:text-slate-100 transition-all duration-300"
            >
              <Close fontSize="medium" />
            </button>
            <h3 className="text-lg text-primary font-semibold ">Scan QR code</h3>
              </div>
            <p className="text-sm text-muted mb-3">
              Scan the QR code or paste the setup key in your authenticator app to continue.
            </p>

            <div className="flex justify-center pt-1 mb-3">
              {mfaSetup?.qrCodeUrl ? (
                <img
                  src={mfaSetup.qrCodeUrl}
                  alt="Scan Code"
                  className="w-52 sm:w-64 h-52 sm:h-64 object-contain border-2 border-line rounded-md shadow-md bg-white"
                />
              ) : null}
            </div>

            <p className="text-center text-primary text-lg mb-3">Scan Code</p>

            <div className="flex items-center justify-center gap-3 mb-3">
              <TextField
                fullWidth
                value={mfaSetup?.secret || ""}
                variant="outlined"
                InputProps={{ readOnly: true }}
                sx={{
                  ...fieldSx,
                  width: "fit-content",
                  "& .MuiOutlinedInput-root": {
                    ...fieldSx["& .MuiOutlinedInput-root"],
                    borderRadius: "12px",
                    backgroundColor: "#F1F5F9",
                    minHeight: {xs: "35px", sm: "48px"},
                  },
                  "& .MuiOutlinedInput-input": {
                    ...fieldSx["& .MuiOutlinedInput-input"],
                    fontSize: "12px",
                    padding: { xs: "9px 6px" ,sm: "12px 14px"},
                  },
                }}
              />
              <Button
                onClick={handleCopySecret}
                variant="contained"
                sx={{
                  bgcolor: "#011A3C",
                  "&:hover": { bgcolor: "#00142F" },
                  borderRadius: "12px",
                  height: {xs: "35px", sm: "48px"},
                  textTransform: "uppercase",
                  gap: 1,
                }}
                className="sm:min-w-[120px]"
              >
                <ContentCopy sx={{ fontSize: 16 }} />
                <span className="hidden sm:block">Copy</span>
              </Button>
            </div>

            <Button
              onClick={handleContinueMfaSetup}
              variant="contained"
              fullWidth
              disabled={!mfaSetup?.secret || !mfaSetup?.qrCodeUrl || isSettingUpMfa}
              sx={{
                bgcolor: "#011A3C",
                "&:hover": { bgcolor: "#00142F" },
                borderRadius: "6px",
                height: {xs: "35px", sm: "48px"},
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Continue
            </Button>
          </div>
        ) : null}

        {mfaSetupStep === "verify" ? (
          <div className="">
            <div className="flex items-center justify-between w-full">
              <button
                type="button"
                onClick={() => setMfaSetupStep("scan")}
                className="flex items-center gap-2 text-primary"
              >
                <ArrowBack fontSize="small" />
                <span className="text-sm">Back</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex justify-end text-primary border-2 border-primary/45 rounded-full hover:bg-primary hover:text-slate-100 transition-all duration-300"
              >
                <Close fontSize="medium" />
              </button>
            </div>
            <div className="lg:mt-6 my-6 lg:mb-8">
              <h3 className="text-4 md:text-lg text-primary font-semibold">Enable Authenticator</h3>
              <p className="text-sm md:text-base text-muted mt-2">
                Enter the 6-digit code from Google Authenticator App to enable authenticator.
              </p>
            </div>

            <p className="text-sm md:text-base text-primary mb-3">Enter Authentication Code</p>

            <div className="flex justify-center mb-7 lg:mb-10 xl:mb-14">
              <MuiOtpInput
                value={mfaCode}
                onChange={handleMfaCodeChange}
                length={6}
                TextFieldsProps={{
                  error: Boolean(mfaVerificationError),
                  inputProps: { inputMode: "numeric" },
                }}
                sx={{
                  width: "100%",
                  maxWidth: { xs: "320px", sm: "100%" },
                  mx: "auto",
                  "& .MuiOtpInput-Box": {
                    width: "100%",
                    justifyContent: { xs: "space-between", sm: "center" },
                    gap: { xs: 0, sm: "20px" },
                    flexWrap: "nowrap",
                  },
                  "& .MuiOutlinedInput-root": {
                    width: { xs: "44px", sm: "54px" },
                    height: { xs: "48px", sm: "54px" },
                    margin: 0,
                    borderRadius: "10px",
                  },
                  "& .MuiOutlinedInput-input": {
                    textAlign: "center",
                    padding: 0,
                    fontSize: { xs: "20px", sm: "22px" },
                    fontWeight: 600,
                  },
                }}
              />
            </div>

            {mfaVerificationError ? (
              <p className="text-xs text-red-600 text-center mb-3">{mfaVerificationError}</p>
            ) : null}

            <Button
              onClick={handleVerifyMfaCode}
              variant="contained"
              fullWidth
              disabled={!isSixDigitCode(mfaCode) || loading || isVerifyingMfa}
              sx={{
                bgcolor: "#011A3C",
                "&:hover": { bgcolor: "#00142F" },
                borderRadius: "6px",
                height: "48px",
                textTransform: "none",
                fontWeight: 700,
              }}
              className="lg:mt-10"
            >
              {isVerifyingMfa ? "Verifying..." : "Enable Authenticator"}
            </Button>
          </div>
        ) : null}

        {mfaSetupStep === "success" ? (
          <div className="space-y-6 lg:space-y-8 xl:space-y-10 py-3">
            <div className="flex justify-center">
              <TwoFASuccessIcon />
            </div>

            <h3 className="text-center text-primary text-3xl font-bold leading-tight">
              Two Factor Authentication Enabled Successfully
            </h3>

            <Button
              onClick={onClose}
              variant="contained"
              fullWidth
              sx={{
                bgcolor: "#011A3C",
                "&:hover": { bgcolor: "#00142F" },
                borderRadius: "6px",
                height: "48px",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Close
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default MfaSetupModal;
